import api from './api';

// All subscription endpoints accept ?env=test|qa|prod so the admin UI can
// target different backend stages from a single deploy (same pattern as
// tenantsService in services/api.js).
//
// Every service method below takes `env` as its last argument, defaulting
// to 'test' so existing calls keep working until the UI is fully wired.

const q = (env) => ({ env: env || 'test' });

// Plans
export const plansService = {
  async getAll(env) {
    const response = await api.get('/api/subscriptions/plans', { params: q(env) });
    return response.data;
  },

  async getById(planId, env) {
    const response = await api.get(`/api/subscriptions/plans/${planId}`, { params: q(env) });
    return response.data;
  },

  async create(planData, env) {
    const response = await api.post('/api/subscriptions/plans', planData, { params: q(env) });
    return response.data;
  },

  async update(planId, planData, env) {
    const response = await api.patch(`/api/subscriptions/plans/${planId}`, planData, { params: q(env) });
    return response.data;
  },

  async delete(planId, env) {
    const response = await api.delete(`/api/subscriptions/plans/${planId}`, { params: q(env) });
    return response.data;
  },
};

// Features
export const featuresService = {
  async getAll(env) {
    const response = await api.get('/api/subscriptions/features', { params: q(env) });
    return response.data;
  },

  async create(featureData, env) {
    const response = await api.post('/api/subscriptions/features', featureData, { params: q(env) });
    return response.data;
  },
};

// Plan Metadata (priceEur, tier, unlockedFeatures, supportTier, freeCreditsIncluded)
export const planMetadataService = {
  async getAll(env) {
    const response = await api.get('/api/subscriptions/plan-metadata', { params: q(env) });
    return response.data;
  },

  async getOptions(env) {
    // Options are static but still accept env for consistency with other calls.
    const response = await api.get('/api/subscriptions/plan-metadata/options', { params: q(env) });
    return response.data;
  },

  async upsert(planId, data, env) {
    const response = await api.put(`/api/subscriptions/plan-metadata/${planId}`, data, { params: q(env) });
    return response.data;
  },
};

// Tenant Subscriptions
export const tenantSubscriptionsService = {
  async create(tenantId, data, env) {
    const response = await api.post(`/api/subscriptions/tenants/${tenantId}/subscriptions`, data, { params: q(env) });
    return response.data;
  },

  async changePlan(tenantId, data, env) {
    const response = await api.patch(`/api/subscriptions/tenants/${tenantId}/subscription/plan`, data, { params: q(env) });
    return response.data;
  },

  async cancel(tenantId, env) {
    const response = await api.delete(`/api/subscriptions/tenants/${tenantId}/subscription`, { params: q(env) });
    return response.data;
  },

  async toggleOverspend(tenantId, enabled, env) {
    const response = await api.patch(`/api/subscriptions/tenants/${tenantId}/subscription/overspend`, { enabled }, { params: q(env) });
    return response.data;
  },

  async getRemaining(tenantId, env) {
    const response = await api.get(`/api/subscriptions/tenants/${tenantId}/remaining`, { params: q(env) });
    return response.data;
  },

  async getSubscription(tenantId, env) {
    const response = await api.get(`/api/subscriptions/tenants/${tenantId}/subscription`, { params: q(env) });
    return response.data;
  },

  async getUsage(tenantId, env) {
    const response = await api.get(`/api/subscriptions/tenants/${tenantId}/usage`, { params: q(env) });
    return response.data;
  },

  async getCycles(tenantId, env) {
    const response = await api.get(`/api/subscriptions/tenants/${tenantId}/cycles`, { params: q(env) });
    return response.data;
  },
};
